import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { ListScreen, TableSkeleton } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Dialog } from '../components/ui/dialog'
import { Field, Textarea } from '../components/ui/field'
import { Blank, Panel } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip, type Tone } from '../components/ui/tone'
import { api, type Contact, type ContactPage, type ImportOutcome } from '../lib/api'
import { useSession } from '../lib/session'

/** One request's worth of rows. The API's own default is 50; this is a deliberate page. */
const PAGE_SIZE = 100

/**
 * `verificationStatus` is a ladder, not a set of tags — §13 walks an address from unverified up to
 * verified, and two of the seven are terminal problems rather than rungs.
 *
 * The tone is the point: this is the field that decides whether an address is safe to send to, so
 * `INVALID` must not look like `VERIFIED`. Everything mid-ladder stays neutral, because colour
 * here means "something is wrong", not "something is happening".
 */
const STATUS: Record<string, { label: string; tone: Tone }> = {
  VERIFIED: { label: 'verified', tone: 'ok' },
  MX_OK: { label: 'mail server ok', tone: 'ok' },
  DOMAIN_OK: { label: 'domain ok', tone: 'neutral' },
  SYNTAX_OK: { label: 'syntax ok', tone: 'neutral' },
  UNVERIFIED: { label: 'unverified', tone: 'warn' },
  RISKY: { label: 'risky', tone: 'warn' },
  INVALID: { label: 'invalid', tone: 'bad' },
}

/**
 * Contacts.
 *
 * Two things this screen refuses to do:
 *
 *   - **Present a page as the whole list.** It asks for 100 rows; if there are more it says
 *     "1–100 of 4,231" rather than printing the true total beside a hundred rows and letting that
 *     read as completeness.
 *   - **Query on every keystroke.** The search is debounced, and changing it resets to the first
 *     page — a filtered result set read at an offset belonging to the previous one is nonsense.
 */
export function ContactsScreen() {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(0)
  const [importing, setImporting] = useState(false)

  // 250ms: long enough that ordinary typing is one request, short enough that the box does not
  // feel like it is ignoring you.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search)
      setPage(0)
    }, 250)
    return () => clearTimeout(timer)
  }, [search])

  const contacts = useQuery({
    queryKey: ['contacts', debounced, page],
    queryFn: () =>
      api.get<ContactPage>(
        `/contacts?take=${PAGE_SIZE}&skip=${page * PAGE_SIZE}` +
          (debounced ? `&search=${encodeURIComponent(debounced)}` : ''),
      ),
    enabled: can('view_contacts'),
    // Keeps the current page on screen while the next loads, so paging does not blink through an
    // empty table.
    placeholderData: (previous) => previous,
  })

  const mayImport = can('import_contacts')

  if (!can('view_contacts')) {
    return (
      <Screen>
        <ScreenHeader title="Contacts" />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to contacts"
            body="Viewing them requires the view_contacts permission."
          />
        </Panel>
      </Screen>
    )
  }

  const data = contacts.data
  const shown = data?.items.length ?? 0
  const total = data?.total ?? 0
  const from = page * PAGE_SIZE
  const hasMore = from + shown < total
  const truncated = shown < total

  return (
    <>
      <ListScreen
        title="Contacts"
        subtitle="People this organization may contact. Suppression is checked separately, immediately before every send."
        query={search}
        onQuery={setSearch}
        queryPlaceholder="Search email or name"
        savedViews={[]}
        primary={
          mayImport ? (
            <Button variant="primary" icon="plus" onClick={() => setImporting(true)}>
              Import
            </Button>
          ) : undefined
        }
        hint={
          data && truncated
            ? `Showing ${(from + 1).toLocaleString()}–${(from + shown).toLocaleString()} of ${total.toLocaleString()}. A page is not the whole list.`
            : undefined
        }
      >
        {contacts.isLoading ? (
          <TableSkeleton columns={5} />
        ) : contacts.isError ? (
          <Blank
            icon="error"
            tone="bad"
            title="Contacts could not be loaded"
            action={<Button onClick={() => void contacts.refetch()}>Try again</Button>}
          />
        ) : shown > 0 ? (
          <>
            <Table>
              <THead>
                <Th>Email</Th>
                <Th width={180}>Name</Th>
                <Th width={220}>Institution</Th>
                <Th width={90}>Country</Th>
                <Th width={150}>Status</Th>
              </THead>
              <TBody>
                {data?.items.map((contact) => (
                  <ContactRow key={contact.id} contact={contact} />
                ))}
              </TBody>
            </Table>

            {(page > 0 || hasMore) && (
              <div className="border-line bg-panel2 flex items-center justify-between border-t px-[14px] py-2.5">
                <p className="text-muted text-[11.5px]">
                  {(from + 1).toLocaleString()}–{(from + shown).toLocaleString()} of{' '}
                  {total.toLocaleString()}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    icon="chevronLeft"
                    disabled={page === 0 || contacts.isFetching}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    trailingIcon="chevronRight"
                    disabled={!hasMore || contacts.isFetching}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : debounced ? (
          <Blank
            icon="noResults"
            title={`Nothing matches “${debounced}”.`}
            body="Search covers email, first and last name."
            action={<Button onClick={() => setSearch('')}>Clear search</Button>}
          />
        ) : (
          <Blank
            icon="contacts"
            title="No contacts yet."
            body={
              mayImport
                ? 'Import addresses to add the first ones.'
                : 'Importing contacts requires the import_contacts permission.'
            }
            action={
              mayImport ? (
                <Button variant="primary" onClick={() => setImporting(true)}>
                  Import contacts
                </Button>
              ) : undefined
            }
          />
        )}
      </ListScreen>

      <ImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        onDone={() => queryClient.invalidateQueries({ queryKey: ['contacts'] })}
      />
    </>
  )
}

function ContactRow({ contact }: { contact: Contact }) {
  const status = STATUS[contact.verificationStatus] ?? {
    label: contact.verificationStatus,
    tone: 'neutral' as Tone,
  }
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')

  return (
    <Tr>
      <Td mono>{contact.canonicalEmail}</Td>
      <Td>{name || <span className="text-faint">—</span>}</Td>
      {/* Institution, not organization. Since P027 the second word means the tenant; a contact's
          university is the first. */}
      <Td className="text-muted">
        {contact.institution?.canonicalName ?? <span className="text-faint">—</span>}
      </Td>
      <Td className="text-muted">{contact.countryCode ?? <span className="text-faint">—</span>}</Td>
      <Td>
        <Chip tone={status.tone}>{status.label}</Chip>
      </Td>
    </Tr>
  )
}

/**
 * Import.
 *
 * One address per line rather than a CSV upload, because that is what the API accepts today and a
 * file picker that silently ignores columns would be copy promising a feature that does not exist.
 * CSV is §95 and comes later.
 *
 * Rejected rows are listed individually with their reason — that is the whole point of the per-row
 * validation the API does (P021), and collapsing it to a count would waste it.
 */
function ImportDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [raw, setRaw] = useState('')
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null)

  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  const run = useMutation({
    mutationFn: () =>
      api.post<ImportOutcome>('/contacts/import', {
        contacts: lines.map((email) => ({ email, source: 'manual entry' })),
      }),
    onSuccess: (result) => {
      setOutcome(result)
      setRaw('')
      onDone()
    },
  })

  function close() {
    setOutcome(null)
    setRaw('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      title="Import contacts"
      description="One email address per line. Re-importing an address updates it rather than duplicating."
      width={520}
      onClose={close}
      footer={
        <>
          <Button variant="quiet" onClick={close}>
            {outcome ? 'Done' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            disabled={lines.length === 0 || run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? 'Importing…' : lines.length > 0 ? `Import ${lines.length}` : 'Import'}
          </Button>
        </>
      }
    >
      <Field label="Addresses">
        {(props) => (
          <Textarea
            {...props}
            rows={6}
            value={raw}
            placeholder={'a.researcher@uni.edu\nk.tanaka@waseda.jp'}
            onChange={(e) => setRaw(e.target.value)}
          />
        )}
      </Field>

      {run.isError && (
        <p role="alert" className="text-bad mt-3 text-[12px]">
          Nothing was imported. The request did not reach the server, or it was refused.
        </p>
      )}

      {outcome && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-1.5">
            <Chip tone={outcome.created > 0 ? 'ok' : 'neutral'}>{outcome.created} created</Chip>
            <Chip tone="neutral">{outcome.updated} updated</Chip>
            {outcome.rejected.length > 0 && (
              <Chip tone="bad">{outcome.rejected.length} rejected</Chip>
            )}
          </div>

          {outcome.rejected.length > 0 && (
            <ul className="border-line mt-3 max-h-40 space-y-1 overflow-y-auto rounded-[7px] border p-2.5">
              {outcome.rejected.map((rejection, index) => (
                // Index in the key: the same malformed line can appear twice in one paste, and two
                // list items with the same key silently drop one.
                <li key={`${rejection.input}-${index}`} className="text-[11.5px]">
                  <span className="font-mono">{rejection.input}</span>{' '}
                  <span className="text-bad">— {rejection.why}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  )
}
